import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  ExternalLink, 
  Calendar, 
  AlertTriangle,
  CheckCircle,
  Activity,
  AlertCircle,
  ThumbsUp,
  ThumbsDown,
  Newspaper
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AnalyzedResult, SentimentBreakdown, SignalAlert } from '@/hooks/useAthleteSignalIntel';

interface SignalTabContentProps {
  analyzedResults: AnalyzedResult[];
  sentimentBreakdown?: SentimentBreakdown;
  alerts: SignalAlert[];
  alertsLabel?: string;
  sentimentLabel?: string;
  coverageLabel?: string;
}

const getSentimentIcon = (sentiment: 'positive' | 'neutral' | 'negative') => {
  switch (sentiment) {
    case 'positive': return <ThumbsUp className="h-3 w-3 text-green-600" />;
    case 'negative': return <ThumbsDown className="h-3 w-3 text-red-600" />;
    default: return <Minus className="h-3 w-3 text-muted-foreground" />;
  }
};

const getSentimentColor = (sentiment: 'positive' | 'neutral' | 'negative') => {
  switch (sentiment) {
    case 'positive': return 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20';
    case 'negative': return 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20';
    default: return 'bg-muted text-muted-foreground border-border';
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'critical': return 'bg-red-600/10 text-red-600 border-red-600/20';
    case 'high': return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
    case 'medium': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
    default: return 'bg-muted text-muted-foreground border-border';
  }
};

const getAlertIcon = (type: string) => {
  switch (type) {
    case 'injury': return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'controversy': return <AlertCircle className="h-4 w-4 text-orange-500" />;
    case 'contract': return <Newspaper className="h-4 w-4 text-blue-500" />;
    case 'achievement': return <TrendingUp className="h-4 w-4 text-green-500" />;
    default: return <Activity className="h-4 w-4" />;
  }
};

export const SignalTabContent: React.FC<SignalTabContentProps> = ({
  analyzedResults,
  sentimentBreakdown,
  alerts,
  alertsLabel = 'Priority Alerts',
  sentimentLabel = 'Sentiment',
  coverageLabel = 'Coverage',
}) => {
  return (
    <div className="space-y-6">
      {/* Priority Alerts - only shown if alerts exist */}
      {alerts.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {alertsLabel}
            <Badge variant="destructive" className="text-xs">{alerts.length}</Badge>
          </h3>
          <div className="space-y-3">
            {alerts.map((alert, i) => (
              <Card key={i} className={cn('border', alert.severity === 'critical' && 'border-red-500/50 bg-red-500/5', alert.severity === 'high' && 'border-orange-500/50 bg-orange-500/5')}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {getAlertIcon(alert.type)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={getPriorityColor(alert.severity)}>{alert.severity.toUpperCase()}</Badge>
                        <Badge variant="outline" className="text-xs capitalize">{alert.type}</Badge>
                      </div>
                      <p className="text-sm">{alert.message}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Sentiment */}
      {sentimentBreakdown && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <Activity className="h-4 w-4" />
            {sentimentLabel}
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                {sentimentBreakdown.overallSentiment === 'positive' ? <TrendingUp className="h-8 w-8 text-green-500" /> :
                 sentimentBreakdown.overallSentiment === 'negative' ? <TrendingDown className="h-8 w-8 text-red-500" /> :
                 <Minus className="h-8 w-8 text-muted-foreground" />}
                <div>
                  <p className="text-2xl font-bold capitalize">{sentimentBreakdown.overallSentiment}</p>
                  <p className="text-sm text-muted-foreground">Overall sentiment from {analyzedResults.length} articles</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">{Math.round(sentimentBreakdown.overallScore)}%</p>
                <p className="text-xs text-muted-foreground">Positivity Score</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 rounded-lg border bg-green-500/5 border-green-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <ThumbsUp className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">Positive</span>
                </div>
                <p className="text-2xl font-bold text-green-600">{sentimentBreakdown.positive}</p>
                <Progress value={(sentimentBreakdown.positive / Math.max(analyzedResults.length, 1)) * 100} className="h-1 mt-2" />
              </div>
              <div className="p-3 rounded-lg border bg-muted/50">
                <div className="flex items-center gap-2 mb-2">
                  <Minus className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Neutral</span>
                </div>
                <p className="text-2xl font-bold">{sentimentBreakdown.neutral}</p>
                <Progress value={(sentimentBreakdown.neutral / Math.max(analyzedResults.length, 1)) * 100} className="h-1 mt-2" />
              </div>
              <div className="p-3 rounded-lg border bg-red-500/5 border-red-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <ThumbsDown className="h-4 w-4 text-red-500" />
                  <span className="text-sm font-medium">Negative</span>
                </div>
                <p className="text-2xl font-bold text-red-600">{sentimentBreakdown.negative}</p>
                <Progress value={(sentimentBreakdown.negative / Math.max(analyzedResults.length, 1)) * 100} className="h-1 mt-2" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Coverage */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          <Newspaper className="h-4 w-4" />
          {coverageLabel}
        </h3>
        {analyzedResults.length === 0 ? (
          <div className="text-center py-8">
            <Newspaper className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">No recent coverage found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {analyzedResults.slice(0, 10).map((item, index) => (
              <a key={index} href={item.link} target="_blank" rel="noopener noreferrer" className="block">
                <Card className="hover:border-primary/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Badge variant="outline" className={cn('shrink-0 mt-0.5', getSentimentColor(item.sentiment))}>
                        {getSentimentIcon(item.sentiment)}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-medium text-sm leading-tight line-clamp-2 hover:text-primary transition-colors">{item.title}</h3>
                          <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{item.snippet}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span className="font-medium">{item.source}</span>
                          {item.publishedDate && (
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(item.publishedDate).toLocaleDateString()}
                            </div>
                          )}
                          <Badge variant="outline" className="text-xs capitalize">{item.category}</Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
